import type { CanvasPayload, PendingBasket } from "../types"
import { BasketView } from "./BasketView"
import { BuildStepCard } from "./BuildStepCard"
import { BuildSummaryCard } from "./BuildSummaryCard"
import { ProductCard } from "./ProductCard"
import { ColorCard } from "./ColorCard"
import { VideoCard } from "./VideoCard"
import { TrackingCard } from "./TrackingCard"
import { KnowledgeCard } from "./KnowledgeCard"
import { SizeGuideCard } from "./SizeGuideCard"

/** Renders the active canvas payload. Dispatches by kind. */
export function CanvasPayloadRouter({
  payload,
  basket,
  onUserReply,
  onAddToCart,
  onRemoveItem,
  onQtyChange,
  onCheckout,
}: {
  payload: CanvasPayload
  basket: PendingBasket | null
  onUserReply: (text: string) => void
  onAddToCart: (kitVariantId: number, addOnNames: string[]) => void
  onRemoveItem: (itemId: string) => void
  onQtyChange: (itemId: string, qty: number) => void
  onCheckout: () => void
}) {
  switch (payload.kind) {
    case "basket":
      return (
        <BasketView
          basket={basket ?? { items: [], subtotal: 0, updated_at: null }}
          onRemove={onRemoveItem}
          onQtyChange={onQtyChange}
          onCheckout={onCheckout}
          onUserReply={onUserReply}
        />
      )
    case "build_step":
      return <BuildStepCard data={payload.data} onChipClick={onUserReply} />
    case "build_summary":
      return (
        <BuildSummaryCard
          data={payload.data}
          onAddToCart={onAddToCart}
          onUserReply={onUserReply}
        />
      )
    case "product":
      // Product cards rendered standalone in the canvas need a max-width
      // constraint or they stretch full-width and look like a billboard
      // instead of a product tile. Center + cap at 360px so they feel
      // like a single product chip even on the wide canvas pane.
      return (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div style={{ width: "100%", maxWidth: 360 }}>
            <ProductCard data={payload.data} />
          </div>
        </div>
      )
    case "products":
      // Density: 150px minmax → ~3-4 columns on the typical canvas pane
      // (was 220px → 2 columns). Lets the canvas surface 6-8 options
      // instead of 4 large cards, which matches how a customer browses a
      // collection page on the storefront. Combined with the dropped
      // description (ProductCard) and 1:1 image crop (widget.module.css),
      // each tile reads as a chip not a billboard.
      return (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
            gap: 8,
          }}
        >
          {payload.data.map((p, i) => <ProductCard key={p.handle ?? i} data={p} />)}
        </div>
      )
    case "color":
      return (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div style={{ width: "100%", maxWidth: 360 }}>
            <ColorCard data={payload.data} />
          </div>
        </div>
      )
    case "colors":
      return (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: 10,
          }}
        >
          {payload.data.map((c, i) => <ColorCard key={c.code ?? i} data={c} />)}
        </div>
      )
    case "video":
      return (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div style={{ width: "100%", maxWidth: 480 }}>
            <VideoCard data={payload.data} />
          </div>
        </div>
      )
    case "tracking":
      return (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div style={{ width: "100%", maxWidth: 360 }}>
            <TrackingCard data={payload.data} />
          </div>
        </div>
      )
    case "knowledge":
      return <KnowledgeCard data={payload.data} />
    case "size_guide":
      return (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div style={{ width: "100%", maxWidth: 480 }}>
            <SizeGuideCard data={payload.data} />
          </div>
        </div>
      )
  }
}
